import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent, Typography, TextField, Button, Alert, Stack, CircularProgress } from '@mui/material';
import WifiIcon from '@mui/icons-material/Wifi';
import { claimSensor } from '../../services/sensorService';

/**
 * Provisions WiFi on a connected sensor. Claims the sensor (minting a device
 * token), then pushes the token and the operator-entered network credentials
 * to the device over serial via SETTOKEN, SETSSID, and SETPASS in order. The
 * password is never logged and is entered in a masked field.
 */
export default function WifiSetupCard({ serialClient, serialNumber, deviceName, onError = undefined }) {
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleConnect = useCallback(async () => {
    setBusy(true);
    setSuccess(false);
    setError(null);
    try {
      const { deviceToken } = await claimSensor(deviceName || serialNumber, serialNumber);
      await serialClient.setToken(deviceToken);
      await serialClient.setSsid(ssid);
      await serialClient.setPass(password);
      setSuccess(true);
    } catch (err) {
      const message = err.message || 'WiFi setup failed';
      setError(message);
      if (onError) onError(message);
    } finally {
      setBusy(false);
    }
  }, [serialClient, serialNumber, deviceName, ssid, password, onError]);

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          WiFi Setup
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect this sensor to a WiFi network to enable cloud telemetry.
        </Typography>

        <Stack spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Network (SSID)"
            value={ssid}
            onChange={(e) => setSsid(e.target.value)}
            fullWidth
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
          />
        </Stack>

        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <WifiIcon />}
          onClick={handleConnect}
          disabled={busy || ssid.length === 0}
        >
          {busy ? 'Connecting...' : 'Connect'}
        </Button>

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Sensor is connecting to WiFi — data will appear on the dashboard shortly.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

WifiSetupCard.propTypes = {
  serialClient: PropTypes.object.isRequired,
  serialNumber: PropTypes.string.isRequired,
  deviceName: PropTypes.string,
  onError: PropTypes.func,
};
